/**
 * Launch-reason breadcrumb (WT-230).
 *
 * The intake can be launched more than once in one page lifetime (Start over, a patient
 * identity reset, a retry, or a re-run nothing asked for). Each launch is numbered and
 * given a reason code. Launches after the first add `?lr=<reason>` to the program-runtime
 * and session-create requests, so the access logs show which code path restarted the
 * intake. Reason codes and booleans only: no tokens, no patient data. The first launch is
 * left exactly as before.
 */
export const LAUNCH_LOG_KEY = "welliemd_intake_launch_log";
const LOG_LIMIT = 20;

export const LAUNCH_REASON = Object.freeze({
  initial: "initial",
  identityReset: "identity_reset",
  startOver: "start_over",
  retry: "retry",
  resumeAfterSignIn: "resume_after_sign_in",
  // A launch that nothing above asked for: the one worth finding.
  effectRerun: "effect_rerun",
});

let launches = 0;
let pendingReason = null;
let currentReason = null;

export const resetLaunchReasonForTests = () => {
  launches = 0;
  pendingReason = null;
  currentReason = null;
};

/** Name the cause of the NEXT launch. Used once, then forgotten. */
export const noteNextLaunch = (reason) => {
  pendingReason = reason;
};

export const readLaunchLog = () => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(LAUNCH_LOG_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
};

/** Record a launch. Returns its reason. Never throws. */
export const beginLaunch = ({ pointerPresent, tokenPresent }) => {
  launches += 1;
  const reason = launches === 1
    ? LAUNCH_REASON.initial
    : (pendingReason || LAUNCH_REASON.effectRerun);
  pendingReason = null;
  currentReason = launches === 1 ? null : reason;

  const entry = {
    n: launches,
    reason,
    pointer: Boolean(pointerPresent),
    token: Boolean(tokenPresent),
    ms: Math.round(typeof performance !== "undefined" ? performance.now() : 0),
  };
  try {
    sessionStorage.setItem(
      LAUNCH_LOG_KEY,
      JSON.stringify([...readLaunchLog(), entry].slice(-LOG_LIMIT)),
    );
  } catch (_error) {
    // Diagnostics must never break a launch.
  }
  if (launches > 1) {
    console.warn(`[intake-launch] launch #${launches} reason=${reason}`, entry);
  }
  return reason;
};

/** Add the launch reason to a runtime request URL, for relaunches only. */
export const withLaunchReason = (url) => (
  currentReason
    ? `${url}${url.includes("?") ? "&" : "?"}lr=${encodeURIComponent(currentReason)}`
    : url
);
